import type { QueryCollectionUtils } from "@tanstack/query-db-collection";
import { replaceEqualDeep } from "@tanstack/react-query";

export interface LocalCollectionLike<T extends { id: string }> {
  delete(keys: string[] | string): {
    isPersisted: {
      promise: Promise<unknown>;
    };
  };
  insert(data: T[] | T): {
    isPersisted: {
      promise: Promise<unknown>;
    };
  };
  state: Map<string, T>;
  update(
    keys: string[],
    callback: (drafts: T[]) => void,
  ): {
    isPersisted: {
      promise: Promise<unknown>;
    };
  };
}

export interface QueryBackedCollectionLike<T extends { id: string }> {
  state: Map<string, T>;
  utils: Pick<
    QueryCollectionUtils<T, string>,
    "writeBatch" | "writeDelete" | "writeUpsert"
  >;
}

// TanStack DB 0.8 exposes these virtual fields on collection reads. They are
// bookkeeping, not persisted row data, and must not force a write on every sync.
const COLLECTION_METADATA_KEYS = new Set([
  "$synced",
  "$origin",
  "$key",
  "$collectionId",
]);

function equalCollectionRows<T extends { id: string }>(
  existing: T,
  next: T,
): boolean {
  if (existing === next) return true;
  const data = Object.fromEntries(
    Object.entries(existing).filter(
      ([key]) => !COLLECTION_METADATA_KEYS.has(key),
    ),
  );
  const nextData = Object.fromEntries(
    Object.entries(next).filter(([key]) => !COLLECTION_METADATA_KEYS.has(key)),
  );
  return replaceEqualDeep(data, nextData) === data;
}

async function syncLocalCollectionRows<T extends { id: string }>(
  collection: LocalCollectionLike<T>,
  nextRows: readonly T[],
  managedKeys: readonly string[],
  options?: {
    mergeExisting?: (existing: T, incoming: T) => T;
    removeMissing?: boolean;
  },
): Promise<void> {
  const nextRowsById = new Map(nextRows.map((row) => [row.id, row] as const));
  const deleteKeys: string[] = [];
  const insertRows: T[] = [];
  const updateRows: Array<readonly [string, T]> = [];
  const seenManagedKeys = new Set<string>();
  // TanStack DB's state getter materializes every row. Snapshot this read
  // phase once; do not rebuild the Map for each managed key.
  const currentRows = collection.state;

  for (const managedKey of managedKeys) {
    if (seenManagedKeys.has(managedKey)) {
      continue;
    }
    seenManagedKeys.add(managedKey);

    const nextRow = nextRowsById.get(managedKey);
    if (!nextRow) {
      if (options?.removeMissing !== false && currentRows.has(managedKey)) {
        deleteKeys.push(managedKey);
      }
      continue;
    }

    const existingRow = currentRows.get(managedKey);
    if (existingRow) {
      const merged = options?.mergeExisting
        ? options.mergeExisting(existingRow, nextRow)
        : nextRow;
      if (!equalCollectionRows(existingRow, merged)) {
        updateRows.push([managedKey, nextRow] as const);
      }
      continue;
    }

    insertRows.push(nextRow);
  }

  if (deleteKeys.length > 0) {
    await collection.delete(deleteKeys).isPersisted.promise;
  }

  if (insertRows.length > 0) {
    await collection.insert(insertRows).isPersisted.promise;
  }

  if (updateRows.length > 0) {
    await collection.update(
      updateRows.map(([key]) => key),
      (drafts) => {
        for (let index = 0; index < drafts.length; index += 1) {
          const nextRow = updateRows[index]?.[1];
          const draft = drafts[index];
          if (!nextRow || !draft) {
            continue;
          }

          Object.assign(
            draft,
            options?.mergeExisting
              ? options.mergeExisting(draft, nextRow)
              : nextRow,
          );
        }
      },
    ).isPersisted.promise;
  }
}

function hasQueryBackedWriteUtils<T extends { id: string }>(
  collection: LocalCollectionLike<T> | QueryBackedCollectionLike<T>,
): collection is QueryBackedCollectionLike<T> {
  return (
    "utils" in collection &&
    typeof collection.utils === "object" &&
    collection.utils !== null &&
    "writeUpsert" in collection.utils &&
    "writeDelete" in collection.utils &&
    "writeBatch" in collection.utils
  );
}

function syncQueryBackedCollectionRows<T extends { id: string }>(
  collection: QueryBackedCollectionLike<T>,
  nextRows: readonly T[],
  managedKeys: readonly string[],
  options?: {
    mergeExisting?: (existing: T, incoming: T) => T;
    removeMissing?: boolean;
  },
): Promise<void> {
  const nextRowsById = new Map(nextRows.map((row) => [row.id, row] as const));
  const deleteKeys: string[] = [];
  const upsertRows: T[] = [];
  const seenManagedKeys = new Set<string>();
  const currentRows = collection.state;

  for (const managedKey of managedKeys) {
    if (seenManagedKeys.has(managedKey)) {
      continue;
    }
    seenManagedKeys.add(managedKey);

    const nextRow = nextRowsById.get(managedKey);
    if (!nextRow) {
      if (options?.removeMissing !== false && currentRows.has(managedKey)) {
        deleteKeys.push(managedKey);
      }
      continue;
    }

    const existingRow = currentRows.get(managedKey);
    const merged =
      existingRow && options?.mergeExisting
        ? options.mergeExisting(existingRow, nextRow)
        : nextRow;
    if (!existingRow || !equalCollectionRows(existingRow, merged)) {
      upsertRows.push(merged);
    }
  }

  if (deleteKeys.length === 0 && upsertRows.length === 0) {
    return Promise.resolve();
  }

  collection.utils.writeBatch(() => {
    if (deleteKeys.length > 0) {
      collection.utils.writeDelete(deleteKeys);
    }

    if (upsertRows.length > 0) {
      collection.utils.writeUpsert(upsertRows);
    }
  });

  return Promise.resolve();
}

export function syncCollectionRows<T extends { id: string }>(
  collection: LocalCollectionLike<T> | QueryBackedCollectionLike<T>,
  nextRows: readonly T[],
  managedKeys: readonly string[],
  options?: {
    mergeExisting?: (existing: T, incoming: T) => T;
    removeMissing?: boolean;
  },
): Promise<void> {
  if (managedKeys.length === 0) return Promise.resolve();

  if (hasQueryBackedWriteUtils(collection)) {
    return syncQueryBackedCollectionRows(
      collection,
      nextRows,
      managedKeys,
      options,
    );
  }

  return syncLocalCollectionRows(collection, nextRows, managedKeys, options);
}
